import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useTheme } from "@/hooks/useTheme";
import { useAuth } from "@/hooks/useAuth";

type ChordPrefix = "g" | "n";

interface Action {
  label: string;
  /** Two-key "G then X" / "N then X" chord — wired below, not just displayed. */
  chord?: { prefix: ChordPrefix; key: string };
  /** Single-key chord combined with Cmd/Ctrl (e.g. "j" → ⌘J). */
  cmdChord?: string;
  run: () => void;
  group: "Navegar" | "Criar" | "Sistema";
}

const CHORD_PREFIX_LABEL: Record<ChordPrefix, string> = { g: "G", n: "N" };

function hintFor(action: Action): string | undefined {
  if (action.chord)
    return `${CHORD_PREFIX_LABEL[action.chord.prefix]} então ${action.chord.key.toUpperCase()}`;
  if (action.cmdChord) return `⌘ ${action.cmdChord.toUpperCase()}`;
  return undefined;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
}

const CHORD_TIMEOUT_MS = 1500;

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { signOut } = useAuth();

  const go = useCallback(
    (path: string) => () => {
      navigate(path);
      setOpen(false);
    },
    [navigate],
  );

  const actions: Action[] = useMemo(
    () => [
      // Navegar — mirrors the sidebar's new IA (Comando / Comercial / Entrega /
      // Presença) plus a few sub-pages that no longer have their own sidebar
      // entry (Issues do Linear, Analytics, Blog) but are still directly routed.
      { group: "Navegar", label: "Comando", chord: { prefix: "g", key: "c" }, run: go("/admin") },
      {
        group: "Navegar",
        label: "Leads & propostas",
        chord: { prefix: "g", key: "l" },
        run: go("/admin/pipeline"),
      },
      { group: "Navegar", label: "Relatório de vendas", run: go("/admin/sales") },
      {
        group: "Navegar",
        label: "Projetos",
        chord: { prefix: "g", key: "p" },
        run: go("/admin/projetos"),
      },
      { group: "Navegar", label: "Issues do Linear", run: go("/admin/linear/issues") },
      {
        group: "Navegar",
        label: "Financeiro",
        chord: { prefix: "g", key: "f" },
        run: go("/admin/finance"),
      },
      { group: "Navegar", label: "Analytics", run: go("/admin/finance?tab=analytics") },
      { group: "Navegar", label: "Conteúdo", run: go("/admin/conteudo") },
      { group: "Navegar", label: "Blog", run: go("/admin/conteudo?tab=blog") },
      { group: "Navegar", label: "Configurações", run: go("/admin/settings") },

      // Criar — quick-create shortcuts. Each jumps to the hub where the actual
      // create action lives (consistent with the pre-existing "Novo orçamento"
      // pattern here — none of these auto-open a dialog from a different page).
      {
        group: "Criar",
        label: "Novo lead",
        chord: { prefix: "n", key: "l" },
        run: go("/admin/pipeline?new=1"),
      },
      {
        group: "Criar",
        label: "Nova proposta (escolher lead no pipeline)",
        chord: { prefix: "n", key: "o" },
        run: go("/admin/pipeline"),
      },
      {
        group: "Criar",
        label: "Abrir issues do Linear",
        chord: { prefix: "n", key: "i" },
        run: go("/admin/linear/issues"),
      },
      {
        group: "Criar",
        label: "Nova despesa",
        chord: { prefix: "n", key: "d" },
        run: go("/admin/finance?tab=expenses"),
      },
      {
        group: "Criar",
        label: "Novo post",
        chord: { prefix: "n", key: "p" },
        run: go("/admin/blog/new"),
      },

      // Sistema
      {
        group: "Sistema",
        label: theme === "dark" ? "Mudar para tema claro" : "Mudar para tema escuro",
        cmdChord: "j",
        run: () => {
          toggleTheme();
          setOpen(false);
        },
      },
      // No global "current payment" context exists to copy a token from — the
      // real per-installment copy-link buttons live on Financeiro's rows
      // (FinanceDashboard.tsx / BudgetPaymentPlan.tsx). This just jumps there.
      { group: "Sistema", label: "Links de pagamento (Financeiro)", run: go("/admin/finance") },
      {
        group: "Sistema",
        label: "Sair da conta",
        run: () => {
          setOpen(false);
          void signOut().then(() => navigate("/admin/login"));
        },
      },
    ],
    [go, theme, toggleTheme, signOut, navigate],
  );

  // Single global keydown listener: ⌘K toggles the palette (unchanged),
  // ⌘J toggles the theme, and a "G then X" / "N then X" pending-prefix chord
  // dispatches the exact same `run()` the matching palette item calls — no
  // duplicated navigation logic. Guarded against firing while typing
  // anywhere (input/textarea/contenteditable) or while the palette itself,
  // or any other Radix dialog/sheet, is open.
  useEffect(() => {
    let pending: ChordPrefix | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const clearPending = () => {
      pending = null;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    // Any surface that owns its own keyboard navigation/typeahead — Radix
    // dialogs/sheets, dropdown menus, selects, comboboxes — should keep
    // exclusive control of keystrokes while open. Concretely: notification
    // titles in this app are DB-seeded starting with "N" ("Novo lead
    // recebido", "Novo case para revisar" — see the notifications
    // migrations), so opening the bell (`role="menu"`) and typing "n" to
    // typeahead-jump to one must NOT also arm the global "n" chord prefix.
    const otherSurfaceOpen = () =>
      !!document.querySelector(
        '[role="dialog"], [role="menu"], [role="listbox"], [role="combobox"]',
      );

    const onKey = (e: KeyboardEvent) => {
      // Ignore auto-repeat from a held key — only the initial keydown should
      // ever arm or resolve a chord.
      if (e.repeat) return;

      // ⌘K / Ctrl+K — toggle the palette itself. Works everywhere, unchanged
      // from before.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
        clearPending();
        return;
      }

      // Everything below is a "shortcut", not a raw keystroke: bail while the
      // user is typing anywhere, or while the palette itself or any other
      // dialog/menu/select/combobox is already open and should own its own
      // keys.
      if (isTypingTarget(e.target) || open || otherSurfaceOpen()) {
        clearPending();
        return;
      }

      const key = e.key.toLowerCase();

      if ((e.metaKey || e.ctrlKey) && key === "j") {
        const themeAction = actions.find((a) => a.cmdChord === "j");
        if (themeAction) {
          e.preventDefault();
          themeAction.run();
        }
        clearPending();
        return;
      }

      // Don't steal any other modifier combo (browser/OS shortcuts), and
      // don't let Shift+letter (e.g. Shift+G) arm a chord like a bare key.
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) {
        clearPending();
        return;
      }

      if (pending) {
        const match = actions.find((a) => a.chord?.prefix === pending && a.chord.key === key);
        clearPending();
        if (match) {
          e.preventDefault();
          match.run();
        }
        return;
      }

      if (key === "g" || key === "n") {
        pending = key;
        timeoutId = setTimeout(clearPending, CHORD_TIMEOUT_MS);
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearPending();
    };
  }, [actions, open]);

  const groupOrder: Action["group"][] = ["Navegar", "Criar", "Sistema"];

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Buscar comandos, navegar, criar…" />
      <CommandList>
        <CommandEmpty>Nenhum resultado.</CommandEmpty>
        {groupOrder.map((g, idx) => (
          <div key={g}>
            {idx > 0 && <CommandSeparator />}
            <CommandGroup heading={g}>
              {actions
                .filter((a) => a.group === g)
                .map((a) => (
                  <CommandItem
                    key={a.label}
                    onSelect={a.run}
                    className="gap-[11px] rounded-[9px] px-[11px] py-2.5"
                  >
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-border"
                      aria-hidden="true"
                    />
                    <span className="flex-1 text-[13.5px]">{a.label}</span>
                    {hintFor(a) && (
                      <CommandShortcut className="font-mono text-[10.5px]">
                        {hintFor(a)}
                      </CommandShortcut>
                    )}
                  </CommandItem>
                ))}
            </CommandGroup>
          </div>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
